import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Group,
  Loader,
  Modal,
  NumberInput,
  Popover,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
  Tooltip,
  useMantineColorScheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  CalendarOff as IconCalendarOff,
  Check as IconCheck,
  ChevronLeft as IconChevronLeft,
  ChevronRight as IconChevronRight,
  Clock as IconClock,
  ExternalLink as IconExternalLink,
  MessageSquare as IconMessageSquare,
  MessageSquareDashed as IconMessageSquareDashed,
  RotateCcw as IconRotateClockwise,
  Send as IconSend,
  Trash2 as IconTrash,
} from "lucide-react";
import { StatusBadge, type TimesheetStatus } from "./StatusBadge";
import { EntryGuardModals, useEntryPersist } from "./entryPersist";
import { MobileHeatmapGrid } from "./MobileHeatmapGrid";
import { formatApprovedBy, formatPendingWith } from "./approverMeta";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import "dayjs/locale/ru";
import {
  copyPreviousWeek,
  createEntry,
  createTimesheet,
  deleteProjectRow,
  fetchProjects,
  fetchTimesheetDetail,
  fetchTimesheets,
  fetchWorkflowConfig,
  submitTimesheet,
  updateEntry,
  type AbsenceInWeek,
  type ProjectOption,
  type TimesheetEntry,
  type TimesheetLrStage,
  type TimesheetWeekDetail,
  type TimesheetWeekSummary,
} from "../api/client";
import { useBadges } from "../hooks/BadgesContext";
import { NEUTRALS, STATUS } from "../theme/tokens";

/** Мобильный режим таймшита: список по дням или heatmap-сетка недели. */
type MobileMode = "list" | "grid";
const MOBILE_MODE_KEY = "skedd:e1-mobile-mode";

function readMobileMode(): MobileMode {
  try {
    return localStorage.getItem(MOBILE_MODE_KEY) === "grid" ? "grid" : "list";
  } catch {
    return "list";
  }
}

const EDITABLE_STATUSES = new Set([
  "draft",
  "rework_requested_by_line_manager",
  "rework_requested_by_project_manager",
  "rolled_back_by_admin",
]);

// Дедлайн отправки — продуктовая конвенция «понедельник 12:00 следующей недели».
// Синхронизировано с текстом напоминаний (backend/apps/api/notifications.py, US-91)
// и cron'ом remind_unsubmitted_weeks (пн 09:00). Меняется процесс — менять оба места.
const SUBMIT_DEADLINE_LABEL = "Пн 12:00";

dayjs.extend(isoWeek);
dayjs.locale("ru");

const DAY_LABELS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const DAY_FULL = [
  "Понедельник",
  "Вторник",
  "Среда",
  "Четверг",
  "Пятница",
  "Суббота",
  "Воскресенье",
];
const GRID_COLUMNS = "260px repeat(7, 1fr) 80px";
const ROW_HEIGHT = 38;
const ABSENCE_FALLBACK_COLOR: Record<AbsenceInWeek["absence_type"], string> = {
  vacation: "#5b6be0",
  // Подтип отпуска «за свой счёт» — чуть светлее планового (поведение то же).
  vacation_unpaid: "#7a87e6",
  // US-78: 3 подтипа отгула — разные оттенки (исходный day_off был #8a6fb8).
  day_off_vacation: "#8a6fb8",
  day_off_weekend: "#6f8fb8",
  day_off_unpaid: "#b87fa8",
  sick_leave: "#c54a64",
};

interface ProjectRow {
  projectId: number;
  projectCode: string;
  projectName: string;
  projectColor: string;
  jiraReference: string | null;
  /** entries keyed by day index 0-6 (Mon-Sun) */
  cells: (TimesheetEntry | null)[];
  total: number;
}

function getMonday(date: dayjs.Dayjs): dayjs.Dayjs {
  return date.isoWeekday(1).startOf("day");
}

// Плашка-переход к неделе, ожидающей правки: причина по статусу.
const ACTION_WEEK_REASON: Record<string, string> = {
  rework_requested_by_line_manager: "возврат от ЛР",
  rework_requested_by_project_manager: "возврат от РП",
  rolled_back_by_admin: "откат администратором",
};

function formatWeekRange(weekStartIso: string): string {
  const start = dayjs(weekStartIso);
  const end = start.add(6, "day");
  return `${start.format("D")}–${end.format("D MMMM")}`;
}

function buildRows(entries: TimesheetEntry[], weekStart: dayjs.Dayjs): ProjectRow[] {
  const map = new Map<number, ProjectRow>();
  for (const entry of entries) {
    if (!map.has(entry.project)) {
      map.set(entry.project, {
        projectId: entry.project,
        projectCode: entry.project_code,
        projectName: entry.project_name,
        projectColor: entry.project_color || "#5b6be0",
        jiraReference: entry.jira_reference || null,
        cells: [null, null, null, null, null, null, null],
        total: 0,
      });
    }
    const row = map.get(entry.project)!;
    if (entry.jira_reference && !row.jiraReference) {
      row.jiraReference = entry.jira_reference;
    }
    const dayIdx = dayjs(entry.work_date).startOf("day").diff(weekStart, "day");
    if (dayIdx >= 0 && dayIdx < 7) {
      // If cell already occupied (duplicate), replace and subtract old hours
      if (row.cells[dayIdx]) {
        row.total -= parseFloat(row.cells[dayIdx]!.hours);
      }
      row.cells[dayIdx] = entry;
      row.total += parseFloat(entry.hours);
    }
  }
  return [...map.values()].sort((a, b) => a.projectCode.localeCompare(b.projectCode));
}

function formatHours(n: number, opts: { dash?: boolean } = {}): string {
  if (!n) return opts.dash ? "—" : "0";
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(1).replace(".", ",");
}

function formatAbsenceHours(hours: string): string {
  const n = parseFloat(hours);
  if (isNaN(n)) return hours;
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(1).replace(".", ",");
}

// ---------------------------------------------------------------------------
// EntryCell — inline number input for a single day×project cell
// ---------------------------------------------------------------------------

interface EntryCellProps {
  entry: TimesheetEntry | null;
  weekId: number;
  projectId: number;
  date: string;
  readonly: boolean;
  isWeekend: boolean;
  isToday: boolean;
  /** US-71: True если день покрыт approved-отпуском сотрудника. Делает
   *  ячейку readonly + показывает tooltip «Дата покрыта отпуском». */
  isAbsenceCovered?: boolean;
  onChanged: () => void;
}

function EntryCell({
  entry,
  weekId,
  projectId,
  date,
  readonly,
  isWeekend,
  isToday,
  isAbsenceCovered = false,
  onChanged,
}: EntryCellProps) {
  // Значение + гейты сохранения — общий хук с мобильным drawer-редактором.
  const {
    value,
    setValue,
    saving,
    validationError,
    setValidationError,
    pendingConfirm,
    setPendingConfirm,
    blockMsg,
    setBlockMsg,
    commit,
    confirmPending,
  } = useEntryPersist({ entry, weekId, projectId, date, onChanged });
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === "dark";
  const neutrals = NEUTRALS[dark ? "dark" : "light"];

  const cellBg = isToday ? neutrals.todayBg : isWeekend ? neutrals.surfaceAlt : "transparent";

  const cellWrapperStyle = {
    height: ROW_HEIGHT,
    background: cellBg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative" as const,
  };

  if (readonly) {
    // US-71: если ячейка readonly из-за approved-отпуска (а не просто
    // weekend/non-editable status), показываем tooltip — иначе пользователь
    // не понимает, почему он не может ввести часы.
    const readonlyCell = (
      <div
        style={{
          ...cellWrapperStyle,
          fontFamily: "var(--mantine-font-family-monospace)",
          fontSize: 12.5,
          fontVariantNumeric: "tabular-nums",
          color: entry ? neutrals.text : neutrals.textDim,
          cursor: isAbsenceCovered ? "not-allowed" : undefined,
        }}
        data-absence-covered={isAbsenceCovered || undefined}
      >
        {entry ? formatHours(parseFloat(entry.hours)) : ""}
        {entry && (
          <EntryCommentPopover entry={entry} weekId={weekId} readonly onChanged={onChanged} />
        )}
      </div>
    );
    if (isAbsenceCovered) {
      return (
        <Tooltip
          label="Дата покрыта одобренным отсутствием. Списать часы нельзя."
          withArrow
          position="top"
        >
          {readonlyCell}
        </Tooltip>
      );
    }
    return readonlyCell;
  }

  return (
    <div style={cellWrapperStyle}>
      <NumberInput
        size="xs"
        min={0}
        max={24}
        step={0.5}
        decimalScale={2}
        // Русский десятичный разделитель: без него «6,5» превращалось в «65»
        // и обрезалось до max 24 (частая жалоба «не могу списать часы»).
        decimalSeparator=","
        // На телефоне — десятичная клавиатура (иначе выскакивает QWERTY).
        inputMode="decimal"
        hideControls
        value={value}
        onChange={(v) => {
          setValue(v);
          setValidationError(false);
        }}
        // US-70: при клике в ячейку — выделяем весь текст. Без этого
        // пользователь должен сначала стереть `0`, потом ввести `8`.
        // С select() — сразу заменяется на введённое значение.
        onFocus={(e) => e.currentTarget.select()}
        onBlur={commit}
        disabled={saving}
        error={validationError}
        styles={{
          input: {
            textAlign: "center",
            width: 56,
            padding: "2px 4px",
            background: "transparent",
            border: "none",
            fontFamily: "var(--mantine-font-family-monospace)",
            fontSize: 12.5,
            fontVariantNumeric: "tabular-nums",
            color: neutrals.text,
            ...(validationError ? { color: "var(--mantine-color-red-6)" } : {}),
          },
        }}
      />
      {entry && (
        <EntryCommentPopover entry={entry} weekId={weekId} readonly={false} onChanged={onChanged} />
      )}
      <EntryGuardModals
        date={date}
        pendingConfirm={pendingConfirm}
        onCancelConfirm={() => setPendingConfirm(null)}
        onConfirm={confirmPending}
        blockMsg={blockMsg}
        onCloseBlock={() => setBlockMsg(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// EntryCommentPopover — icon-anchored popover for entry.comment field
// ---------------------------------------------------------------------------

interface EntryCommentPopoverProps {
  entry: TimesheetEntry;
  weekId: number;
  readonly: boolean;
  onChanged: () => void;
}

function EntryCommentPopover({ entry, weekId, readonly, onChanged }: EntryCommentPopoverProps) {
  const [opened, setOpened] = useState(false);
  const [draft, setDraft] = useState(entry.comment ?? "");
  const [saving, setSaving] = useState(false);
  const hasComment = (entry.comment ?? "").trim().length > 0;

  // Reset draft when entry identity changes or popover opens
  useEffect(() => {
    if (opened) setDraft(entry.comment ?? "");
  }, [opened, entry.id, entry.comment]);

  const save = useCallback(async () => {
    if (saving) return;
    if ((draft ?? "") === (entry.comment ?? "")) {
      setOpened(false);
      return;
    }
    setSaving(true);
    try {
      await updateEntry(weekId, entry.id, { comment: draft });
      onChanged();
      setOpened(false);
    } finally {
      setSaving(false);
    }
  }, [draft, entry.comment, entry.id, weekId, saving, onChanged]);

  const Icon = hasComment ? IconMessageSquare : IconMessageSquareDashed;

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      width={260}
      withArrow
      shadow="md"
      trapFocus
    >
      <Popover.Target>
        <Tooltip
          label={
            hasComment ? (readonly ? entry.comment : "Изменить пояснение") : "Добавить пояснение"
          }
          openDelay={300}
          withArrow
          multiline
          maw={260}
        >
          <ActionIcon
            size="sm"
            variant={hasComment ? "filled" : "light"}
            color={hasComment ? "brand" : "gray"}
            radius="xl"
            onClick={() => setOpened((o) => !o)}
            aria-label={hasComment ? `Пояснение: ${entry.comment}` : "Добавить пояснение к строке"}
            data-has-comment={hasComment ? "true" : "false"}
            style={{
              position: "absolute",
              top: 2,
              right: 2,
              // Пустую иконку раньше приходилось искать пикселхантингом (0.55 на
              // светлой подсветке «сегодня» была почти невидима). Делаем заметнее.
              opacity: hasComment ? 1 : 0.72,
              boxShadow: hasComment ? "0 1px 2px rgba(0,0,0,0.18)" : undefined,
            }}
          >
            <Icon size={13} strokeWidth={2.2} />
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <Text size="xs" fw={600}>
            Пояснение к списанию
          </Text>
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            readOnly={readonly}
            placeholder={
              readonly
                ? "Пояснение не указано"
                : "Например: внутренняя задача, невозможно привязать Jira"
            }
            autosize
            minRows={3}
            maxRows={8}
            maxLength={2000}
            data-testid="entry-comment-textarea"
          />
          {!readonly && (
            <Group gap="xs" justify="flex-end">
              <Button
                size="xs"
                variant="default"
                onClick={() => setOpened(false)}
                disabled={saving}
              >
                Отмена
              </Button>
              <Button size="xs" onClick={save} loading={saving}>
                Сохранить
              </Button>
            </Group>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Composite week picker — bordered pill + Today button
// ---------------------------------------------------------------------------

interface WeekPickerProps {
  weekStart: dayjs.Dayjs;
  onChange: (d: dayjs.Dayjs) => void;
}

function WeekPicker({ weekStart, onChange }: WeekPickerProps) {
  const weekEnd = weekStart.add(6, "day");
  const sameMonth = weekStart.month() === weekEnd.month();
  const label = sameMonth
    ? `${weekStart.format("DD")} – ${weekEnd.format("DD MMM")}`
    : `${weekStart.format("DD MMM")} – ${weekEnd.format("DD MMM")}`;
  const wMeta = `W${weekStart.isoWeek()} · ${weekStart.format("YYYY")}`;

  const { colorScheme } = useMantineColorScheme();
  const neutrals = NEUTRALS[colorScheme === "dark" ? "dark" : "light"];
  // На узком экране прячем мету «W18 · 2024» — из-за неё диапазон дат не влезал
  // в строку и переносился/обрезался. Дату оставляем, номер недели — в тултипе.
  const narrow = useMediaQuery("(max-width: 30em)");

  return (
    <Group gap="xs" wrap="nowrap">
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          border: `1px solid ${neutrals.border}`,
          borderRadius: 5,
          background: neutrals.surface,
          height: 32,
        }}
      >
        <ActionIcon
          variant="subtle"
          size={30}
          onClick={() => onChange(weekStart.subtract(7, "day"))}
          aria-label="Прошлая неделя"
          style={{ borderRadius: 0 }}
        >
          <IconChevronLeft size={14} />
        </ActionIcon>
        <div
          title={narrow ? wMeta : undefined}
          style={{
            padding: narrow ? "0 10px" : "0 14px",
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            borderLeft: `1px solid ${neutrals.border}`,
            borderRight: `1px solid ${neutrals.border}`,
            height: "100%",
            lineHeight: "32px",
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 13, color: neutrals.text }}>{label}</span>
          {!narrow && (
            <span
              style={{
                color: neutrals.textDim,
                fontFamily: "var(--mantine-font-family-monospace)",
                fontSize: 11,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {wMeta}
            </span>
          )}
        </div>
        <ActionIcon
          variant="subtle"
          size={30}
          onClick={() => onChange(weekStart.add(7, "day"))}
          aria-label="Следующая неделя"
          style={{ borderRadius: 0 }}
        >
          <IconChevronRight size={14} />
        </ActionIcon>
      </div>
      <Button variant="subtle" size="xs" onClick={() => onChange(getMonday(dayjs()))} color="gray">
        Сегодня
      </Button>
    </Group>
  );
}

// ---------------------------------------------------------------------------
// Summary KPI cards
// ---------------------------------------------------------------------------

interface SummaryCardProps {
  label: string;
  value: string;
  unit?: string;
  hint?: string;
  tone?: "default" | "warn" | "accent";
}

/**
 * Подпись под «Осталось списать». Дни заявки на отсутствие, ещё не
 * согласованной, закрыты для ввода часов, но норму не уменьшают — без
 * пояснения человек ищет, куда списать часы, которые списать нельзя.
 * Текст — владелицы (25.09.2026), тот же смысл, что в окне отправки.
 */
function remainingHint(unallocated: number, pendingAbsence: number): string {
  if (pendingAbsence <= 0) return "до отправки";
  const pendingInDebt = Math.min(pendingAbsence, unallocated);
  if (unallocated - pendingInDebt <= 0.001) {
    return "все — дни заявки на отсутствие, находящейся на согласовании";
  }
  return `из них ${formatHours(pendingInDebt)} ч — дни заявки на отсутствие, находящейся на согласовании`;
}

function SummaryCard({ label, value, unit, hint, tone = "default" }: SummaryCardProps) {
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === "dark";
  const neutrals = NEUTRALS[dark ? "dark" : "light"];
  const status = STATUS[dark ? "dark" : "light"];
  const valueColor =
    tone === "warn" ? status.rework.fg : tone === "accent" ? status.approved.fg : neutrals.text;

  return (
    <div
      style={{
        padding: "12px 16px",
        border: `1px solid ${neutrals.border}`,
        borderRadius: 8,
        background: neutrals.surface,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: neutrals.textMuted,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: 4,
          display: "flex",
          alignItems: "baseline",
          gap: 4,
          fontWeight: 600,
          color: valueColor,
        }}
      >
        <span
          style={{
            fontSize: 24,
            letterSpacing: "-0.02em",
            fontVariantNumeric: "tabular-nums",
            fontFamily: "var(--mantine-font-family-monospace)",
          }}
        >
          {value}
        </span>
        {unit && (
          <span style={{ fontSize: 13, color: neutrals.textMuted, fontWeight: 500 }}>{unit}</span>
        )}
      </div>
      {hint && <div style={{ marginTop: 4, fontSize: 11.5, color: neutrals.textDim }}>{hint}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status banners
// ---------------------------------------------------------------------------

interface BannerProps {
  kind: "info" | "approved" | "rework";
  title: string;
  body?: string;
  meta?: string;
  icon?: React.ReactNode;
}

function StatusBanner({ kind, title, body, meta, icon }: BannerProps) {
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === "dark";
  const tok =
    kind === "approved"
      ? STATUS[dark ? "dark" : "light"].approved
      : kind === "rework"
        ? STATUS[dark ? "dark" : "light"].rework
        : STATUS[dark ? "dark" : "light"].submitted;

  return (
    <div
      style={{
        background: tok.bg,
        color: tok.fg,
        border: `1px solid ${tok.border}`,
        borderRadius: 8,
        padding: "12px 14px",
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
      }}
    >
      {icon && <div style={{ paddingTop: 1 }}>{icon}</div>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: body ? 4 : 0 }}>{title}</div>
        {body && <div style={{ fontSize: 12.5, lineHeight: 1.5, opacity: 0.9 }}>{body}</div>}
        {meta && (
          <div
            style={{
              marginTop: 8,
              fontSize: 11,
              fontFamily: "var(--mantine-font-family-monospace)",
              opacity: 0.7,
            }}
          >
            {meta}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Absence-only state — full card replacing the grid when status=absence_only
// ---------------------------------------------------------------------------

function AbsenceOnlyCard() {
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === "dark";
  const neutrals = NEUTRALS[dark ? "dark" : "light"];
  const slot = STATUS[dark ? "dark" : "light"].absence;

  return (
    <div
      style={{
        border: `1px solid ${neutrals.border}`,
        borderRadius: 8,
        background: neutrals.surface,
        padding: "40px 32px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 14,
        textAlign: "center",
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 999,
          background: slot.bg,
          color: slot.fg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: `1px solid ${slot.border}`,
        }}
      >
        <IconCalendarOff size={22} />
      </div>
      <div>
        <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>
          Неделя полностью покрыта отсутствием
        </div>
        <div style={{ color: neutrals.textMuted, fontSize: 13, maxWidth: 460 }}>
          Все рабочие дни недели закрыты утверждённым отсутствием. Таймшит на эту неделю не
          создаётся.
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add-project ghost button + searchable picker (kept as Mantine Select inside Popover-like)
// ---------------------------------------------------------------------------

interface AddProjectButtonProps {
  projects: ProjectOption[];
  usedProjectIds: Set<number>;
  weekId: number;
  weekStart: dayjs.Dayjs;
  onAdded: () => void;
}

interface AddProjectButtonExtraProps {
  absenceCoveredDates: Set<string>;
  workingDayIndices: number[];
  onError: (msg: string) => void;
}

function AddProjectButton({
  projects,
  usedProjectIds,
  weekId,
  weekStart,
  onAdded,
  absenceCoveredDates,
  workingDayIndices,
  onError,
}: AddProjectButtonProps & AddProjectButtonExtraProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const options = projects
    .filter((p) => !usedProjectIds.has(p.id))
    .map((p) => ({ value: String(p.id), label: `${p.code} — ${p.name}` }));

  if (options.length === 0) return null;

  // US-74: плейсхолдер entry (hours=0) создаём на ПЕРВЫЙ рабочий день недели,
  // НЕ покрытый approved-отсутствием. Раньше всегда брали weekStart (Пн),
  // и при отгуле на Пн backend US-71 валидация отбивала 400 — фронт молча
  // проглатывал, строка проекта не появлялась.
  const pickPlaceholderDate = (): string | null => {
    for (const dayIdx of workingDayIndices) {
      const d = weekStart.add(dayIdx, "day").format("YYYY-MM-DD");
      if (!absenceCoveredDates.has(d)) return d;
    }
    return null;
  };

  const handleAdd = async (id: string | null) => {
    if (!id) return;
    const placeholderDate = pickPlaceholderDate();
    if (!placeholderDate) {
      // Все рабочие дни покрыты absence — добавлять некуда. Это absence_only
      // неделя, AddProjectButton не должна была отрисоваться (на уровне грида
      // проверка !isAbsenceOnly), но защищаемся на случай гонок.
      onError("В этой неделе нет свободных рабочих дней — все покрыты отсутствием.");
      setSelectedId(null);
      return;
    }
    setSaving(true);
    try {
      await createEntry(weekId, {
        work_date: placeholderDate,
        project: parseInt(id, 10),
        hours: "0.00",
      });
      setSelectedId(null);
      onAdded();
    } catch (e) {
      // US-74: раньше catch отсутствовал — ошибки 400 от backend молча
      // проглатывались, строка не появлялась, пользователь не понимал почему.
      const msg = e instanceof Error ? e.message : "Не удалось добавить проект.";
      onError(msg);
      setSelectedId(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Select
      size="xs"
      placeholder="+ Добавить проект"
      data={options}
      value={selectedId}
      onChange={(v) => {
        setSelectedId(v);
        if (v) handleAdd(v);
      }}
      searchable
      disabled={saving}
      // Поле в верхней строке таймшита уже, чем было (200 px): иначе строка не
      // помещалась в одну линию на окне 1400 px с полосой прокрутки. Список
      // проектов шире поля и раскрывается влево — названия «КОД — имя» длинные.
      style={{ width: 160 }}
      comboboxProps={{ width: 280, position: "bottom-end" }}
    />
  );
}

// ---------------------------------------------------------------------------
// TimesheetGrid — main component
// ---------------------------------------------------------------------------

export interface TimesheetGridProps {
  /** US-90: неделя из URL-маршрута (/timesheet/:weekStart). Без prop —
   * внутренний стейт с дефолтом «текущая неделя» (тесты, standalone). */
  weekStart?: dayjs.Dayjs;
  /** US-90: вызывается при смене недели (WeekPicker). В роутер-режиме
   * родитель делает navigate(/timesheet/<date>). */
  onWeekChange?: (next: dayjs.Dayjs) => void;
}

export function TimesheetGrid({ weekStart: weekStartProp, onWeekChange }: TimesheetGridProps = {}) {
  const [internalWeekStart, setInternalWeekStart] = useState(() => getMonday(dayjs()));
  // Нормализуем prop к понедельнику — deep-link может прислать любой день.
  const weekStart = useMemo(
    () => (weekStartProp ? getMonday(weekStartProp) : internalWeekStart),
    [weekStartProp, internalWeekStart],
  );
  const setWeekStart = onWeekChange ?? setInternalWeekStart;
  const [timesheets, setTimesheets] = useState<TimesheetWeekSummary[]>([]);
  const [detail, setDetail] = useState<TimesheetWeekDetail | null>(null);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Отдельно от `error`: сбой ЗАГРУЗКИ недели/списка. Нужен, чтобы отличить
  // «недели нет» от «не смогли узнать, есть ли она» — иначе 503 от стенда
  // рисовался как «Таймшит на эту неделю ещё не создан» (найдено 16.07).
  const [loadFailed, setLoadFailed] = useState<string | null>(null);
  // US-72: workflow config используется для текста баннера статуса
  // `waiting_project_managers`. На стенде prod сейчас `none` → ЛР не в
  // цепочке, и старый хардкод «ЛР одобрил неделю» вводит в заблуждение.
  // null до первого fetch; fallback при null → как `before_pm` (legacy).
  const [lrStage, setLrStage] = useState<TimesheetLrStage | null>(null);
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === "dark";
  const neutrals = NEUTRALS[dark ? "dark" : "light"];
  // Мобильный режим: узкий экран → таймшит-сетка (≈900px) не влезает.
  // Рендерим вместо неё компактный список по дням (day-major).
  const isMobile = useMediaQuery("(max-width: 48em)");
  // Мобильный режим отображения: «Список» (день-мажорный, ввод по одному дню)
  // или «Сетка» (heatmap-обзор недели + drawer-редактор). Выбор запоминаем —
  // у людей разные привычки. localStorage может быть недоступен → try/catch.
  const [mobileMode, setMobileMode] = useState<MobileMode>(readMobileMode);
  const setMobileModePersisted = useCallback((m: MobileMode) => {
    setMobileMode(m);
    try {
      localStorage.setItem(MOBILE_MODE_KEY, m);
    } catch {
      // приватный режим / отключён — не критично, останется на сессию
    }
  }, []);

  const weekKey = weekStart.format("YYYY-MM-DD");
  const todayIdx = useMemo(() => {
    const idx = dayjs().startOf("day").diff(weekStart, "day");
    return idx >= 0 && idx < 7 ? idx : -1;
  }, [weekStart]);

  // US-53: производственный календарь недели (7 элементов Mon-Sun).
  // Берём из detail.days; если нет (старый формат backend / пустой detail) —
  // fallback по JS weekday (Mon-Fri = work, Sat-Sun = holiday). Используется
  // для стилизации ячеек (нерабочие — серый фон) и блокировки ввода в выходные.
  const dayInfos = useMemo(() => {
    if (detail?.days?.length === 7) return detail.days;
    return Array.from({ length: 7 }, (_, i) => {
      const date = weekStart.add(i, "day");
      const isWeekend = i >= 5;
      return {
        date: date.format("YYYY-MM-DD"),
        day_type: (isWeekend ? "holiday" : "work") as "work" | "shortened" | "holiday" | "transfer",
        standard_hours: isWeekend ? "0.00" : "8.00",
      };
    });
  }, [detail, weekStart]);

  // День нерабочий, если у него норма часов 0 (универсальный признак:
  // покрывает обычные Sat/Sun-выходные, праздники, перенесённые выходные).
  const isNonWorkingDay = useCallback(
    (idx: number) => {
      const info = dayInfos[idx];
      if (!info) return idx >= 5;
      // standard_hours приходит как "0.00" / "8.00" / "7.00" — сравниваем как число.
      return parseFloat(info.standard_hours) === 0;
    },
    [dayInfos],
  );

  // US-74: список РАБОЧИХ дней недели (индексы 0=Пн ... 6=Вс) — нужен для
  // AddProjectButton чтобы выбрать плейсхолдер-дату НЕ покрытую absence.
  const workingDayIndices = useMemo(
    () => Array.from({ length: 7 }, (_, i) => i).filter((i) => !isNonWorkingDay(i)),
    [isNonWorkingDay],
  );

  // Норма часов по дням недели (для интенсивности heatmap и readonly-гейта).
  const dayNorms = useMemo(
    () => Array.from({ length: 7 }, (_, i) => parseFloat(dayInfos[i]?.standard_hours ?? "0")),
    [dayInfos],
  );

  // Подпись типа дня в tooltip — пользователь видит ПОЧЕМУ ячейка серая.
  const dayTypeLabel = useCallback(
    (idx: number) => {
      const info = dayInfos[idx];
      if (!info) return "";
      switch (info.day_type) {
        case "holiday":
          return "Праздник / выходной";
        case "transfer":
          return "Перенос — нерабочий день";
        case "shortened":
          return "Сокращённый рабочий день";
        default:
          return "";
      }
    },
    [dayInfos],
  );

  // Find existing timesheet for selected week
  const currentWeek = useMemo(
    () => timesheets.find((t) => t.week_start === weekKey),
    [timesheets, weekKey],
  );

  // Load timesheet list + projects
  const loadList = useCallback(async () => {
    try {
      const [ts, prj] = await Promise.all([fetchTimesheets(), fetchProjects()]);
      setTimesheets(ts);
      setProjects(prj);
      setLoadFailed(null);
    } catch (err: unknown) {
      // НЕ setError: иначе ниже отрендерится «Таймшит ещё не создан» — сбой
      // загрузки выглядел бы как «ваши часы пропали». Список не приехал ⇒ мы
      // просто НЕ ЗНАЕМ, есть неделя или нет.
      setLoadFailed(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    loadList().finally(() => setLoading(false));
  }, [loadList]);

  // US-72: один раз на mount подгружаем `WorkflowConfig.timesheet_lr_stage`,
  // чтобы баннер `waiting_project_managers` показывал правильный текст
  // в зависимости от того, был ли ЛР в цепочке. Ошибки игнорируем — fallback
  // на `before_pm` (legacy default) даст старый текст.
  useEffect(() => {
    fetchWorkflowConfig()
      .then((cfg) => setLrStage(cfg.timesheet_lr_stage))
      .catch(() => {
        // тихо; баннер останется в legacy-варианте
      });
  }, []);

  // Load detail when week changes (depend on id, not object reference)
  const currentWeekId = currentWeek?.id ?? null;
  useEffect(() => {
    if (!currentWeekId) {
      setDetail(null);
      return;
    }
    setLoading(true);
    fetchTimesheetDetail(currentWeekId)
      .then((d) => {
        setDetail(d);
        setLoadFailed(null);
      })
      .catch((err: Error) => setLoadFailed(err.message))
      .finally(() => setLoading(false));
  }, [currentWeekId]);

  // Сбрасываем плашку ошибки при переключении недели — иначе ошибка от
  // создания/загрузки одной недели «залипает» и висит на всех следующих
  // (баг: пользователь видел «Таймшит уже существует» на неделях, где его нет).
  useEffect(() => {
    setError(null);
    setLoadFailed(null);
  }, [weekKey]);

  // Create timesheet for current week
  const handleCreate = async () => {
    setLoading(true);
    setError(null);
    try {
      const created = await createTimesheet(weekKey);
      setDetail(created);
      await loadList();
    } catch (err: unknown) {
      // Двойной клик / гонка загрузки: неделя уже создана — не пугаем баннером,
      // а перезагружаем список, чтобы существующая неделя сама подтянулась.
      const msg = err instanceof Error ? err.message : String(err);
      await loadList();
      if (!/уже существует/i.test(msg)) {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Повтор после сбоя загрузки: тянем и список недель, и деталь текущей.
  const retryLoad = useCallback(async () => {
    setLoadFailed(null);
    setLoading(true);
    try {
      await loadList();
      if (currentWeekId) {
        setDetail(await fetchTimesheetDetail(currentWeekId));
        setLoadFailed(null);
      }
    } catch (err: unknown) {
      setLoadFailed(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [loadList, currentWeekId]);

  // Reload detail after entry change
  const reload = useCallback(async () => {
    if (!currentWeekId) return;
    try {
      const d = await fetchTimesheetDetail(currentWeekId);
      setDetail(d);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [currentWeekId]);

  // US-89: «Повторить прошлую неделю» — копирует ТОЛЬКО проекты и часы
  // прошлой недели (Jira/комментарии не копируются), submit остаётся ручным.
  // Backend пропускает занятые ячейки, дни approved-отсутствия и архивные
  // проекты — итог показываем плашкой.
  const [copying, setCopying] = useState(false);
  const [copyInfo, setCopyInfo] = useState<string | null>(null);
  const handleCopyPreviousWeek = useCallback(async () => {
    if (!currentWeekId) return;
    setError(null);
    setCopyInfo(null);
    setCopying(true);
    try {
      const res = await copyPreviousWeek(currentWeekId);
      const parts = [`Скопировано строк: ${res.created}`];
      if (res.skipped_existing > 0) {
        parts.push(`занятые ячейки пропущены: ${res.skipped_existing}`);
      }
      if (res.skipped_absence > 0) {
        parts.push(`дни отсутствия пропущены: ${res.skipped_absence}`);
      }
      if (res.skipped_archived.length > 0) {
        parts.push(`архивные проекты пропущены: ${res.skipped_archived.join(", ")}`);
      }
      setCopyInfo(parts.join(" · "));
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCopying(false);
    }
  }, [currentWeekId, reload]);

  const rows = useMemo(
    () => (detail ? buildRows(detail.entries, weekStart) : []),
    [detail, weekStart],
  );

  const isEditable = detail ? EDITABLE_STATUSES.has(detail.status) : false;
  const required = detail ? parseFloat(detail.required_hours) : 0;
  const allocated = detail ? parseFloat(detail.allocated_hours) : 0;
  const unallocated = detail ? parseFloat(detail.unallocated_hours) : 0;
  // Часы, которые нельзя списать: дни закрыты заявкой на отсутствие, ещё не
  // прошедшей маршрут. В норму они не входят, поэтому попадают в «недосписано»
  // — и окно отправки обязано назвать эту причину, а не советовать проверить
  // забытые дни. 20.09.2026: система запретила заполнить два дня и сама же
  // отчитала за них.
  const pendingAbsence = detail ? parseFloat(detail.pending_absence_hours ?? "0") : 0;

  // US-74 hotfix: «Норма недели» в KPI-карточке — это часы, которые ЧЕЛОВЕК
  // должен списать руками (т.е. календарная норма МИНУС approved-отсутствия).
  // Backend хранит `required_hours` как чисто календарную (40ч за полную
  // рабочую неделю при ставке 1.0), не вычитая absence. Из-за этого при
  // частичном отгуле (например Пн+Вт=16ч) KPI показывал «Норма 40 / Списано 0
  // / Осталось 24» — арифметика 40-0=40 ≠ 24 сбивает с толку.
  // Считаем absence-часы по `detail.absences[].days[].hours` и вычитаем,
  // чтобы выводимая норма = реальный workload на этой неделе.
  const absenceHoursInWeek = useMemo(() => {
    if (!detail) return 0;
    return detail.absences.reduce(
      (sum, abs) => sum + abs.days.reduce((dsum, d) => dsum + parseFloat(d.hours), 0),
      0,
    );
  }, [detail]);
  const displayedRequired = Math.max(0, required - absenceHoursInWeek);

  // US-71: дни, на которые у сотрудника уже approved-отсутствие (отпуск /
  // отгул / больничный). Backend на эти даты возвращает 400 «нельзя
  // списать». На фронте сразу делаем ячейки readonly с tooltip, чтобы
  // пользователь не пытался впустую кликнуть. Set хранит "YYYY-MM-DD".
  const absenceCoveredDates = useMemo(() => {
    const set = new Set<string>();
    if (!detail) return set;
    for (const abs of detail.absences) {
      for (const day of abs.days) {
        set.add(day.date);
      }
    }
    return set;
  }, [detail]);
  // US-55: пустая рабочая неделя — нечего отправлять. Backend вернёт 400
  // если allocated == 0 && нет approved absences, при норме > 0. Дисейблим
  // кнопку на фронте чтобы не тыкали зря.
  // NB: проверяем allocated_hours (а не entries.length), потому что это
  // канонический счётчик с бэка — он 1:1 с backend-логикой блокировки.
  const isEmptyWeek = !!detail && allocated === 0 && detail.absences.length === 0 && required > 0;
  // US-46: submit разрешён при любом unallocated (включая > 0 «недосписано»).
  // Раньше canSubmit = unallocated <= 0 запрещал кнопку до полного заполнения.
  // Теперь — кнопка активна всегда (в editable-статусе), но при unallocated != 0
  // показываем confirm-модалку перед фактической отправкой.
  // US-55: дополнительно запрещаем submit пустой рабочей недели.
  const canSubmit = isEditable && !isEmptyWeek;
  const usedProjectIds = useMemo(() => new Set(rows.map((r) => r.projectId)), [rows]);

  // US-45: модалка подтверждения удаления строки проекта
  const [deletingProject, setDeletingProject] = useState<{
    projectId: number;
    projectCode: string;
    projectName: string;
    totalHours: number;
  } | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  const handleConfirmDeleteProject = useCallback(async () => {
    if (!detail || !deletingProject) return;
    setDeletingBusy(true);
    setError(null);
    try {
      await deleteProjectRow(detail.id, deletingProject.projectId);
      await reload();
      setDeletingProject(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeletingBusy(false);
    }
  }, [detail, deletingProject, reload]);

  // US-46: модалка подтверждения отправки при unallocated != 0.
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);

  // US-59 #8: refresh бейджей сразу после submit — иначе кружок «свои
  // действия» висит до 30s polling-тика.
  const { badges, refresh: refreshBadges } = useBadges();

  // Плашка-переход к проблемной неделе (кейс Шейкина, 13.07): бейдж «Таймшит»
  // показывает цифру по возвращённым/откаченным неделям, но таймшит открывается
  // на текущей — и где действие, человек ищет листанием. Показываем указатель
  // на все недели-должники, КРОМЕ открытой сейчас (на ней уже есть баннер
  // возврата с комментарием).
  const pendingFixWeeks = useMemo(
    () => (badges?.my_timesheet_action_weeks ?? []).filter((w) => w.week_start !== weekKey),
    [badges, weekKey],
  );

  const executeSubmit = useCallback(async () => {
    if (!detail) return;
    setLoading(true);
    setError(null);
    try {
      const updated = await submitTimesheet(detail.id);
      setDetail(updated);
      void refreshBadges();
      await loadList();
      setSubmitConfirmOpen(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [detail, loadList, refreshBadges]);

  const handleSubmit = useCallback(async () => {
    if (!detail) return;
    // US-46: если есть отклонение от нормы — спрашиваем подтверждение.
    // Проверяем строго (`Math.abs > 0`), чтобы negative-zero (`-0` из
    // parseFloat("-0.00")) не открывал модалку — оно `!== 0` в строгом
    // JS-сравнении даёт false, но эквивалент 0.
    if (Math.abs(unallocated) > 0) {
      setSubmitConfirmOpen(true);
      return;
    }
    await executeSubmit();
  }, [detail, unallocated, executeSubmit]);

  // US-46 fix (2026-05-23): если пока модалка открыта пользователь добавил
  // часы и `unallocated` стал 0 — модалка с текстом «на 0 ч больше нормы»
  // выглядит ложно. Автозакрываем её. Если хочет подтвердить — снова жмёт
  // «Отправить на согласование» и форма уйдёт без модалки.
  useEffect(() => {
    if (submitConfirmOpen && Math.abs(unallocated) === 0) {
      setSubmitConfirmOpen(false);
    }
  }, [submitConfirmOpen, unallocated]);

  // Day column totals
  const dayTotals = useMemo(() => {
    const totals = [0, 0, 0, 0, 0, 0, 0];
    for (const row of rows) {
      for (let i = 0; i < 7; i++) {
        if (row.cells[i]) totals[i] += parseFloat(row.cells[i]!.hours);
      }
    }
    return totals;
  }, [rows]);

  // Day dates for header
  const dayDates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => weekStart.add(i, "day")),
    [weekStart],
  );

  const isAbsenceOnly = detail?.status === "absence_only";
  // Память согласований (13.07): в статусе «Возврат от РП» помечаем строки
  // проектов, чьи подписи пережили возврат, — их лучше не трогать без нужды
  // (правка снимет подпись, и проект пере-согласуется).
  const approvedProjectIds = useMemo(() => new Set(detail?.approved_project_ids ?? []), [detail]);
  const showApprovedMarks =
    detail?.status === "rework_requested_by_project_manager" && approvedProjectIds.size > 0;
  // US-85: ФИО «у кого на согласовании» (на статусах ожидания) и «кто согласовал»
  // (на финальном approved) — подмешиваем в баннеры статуса ниже.
  const pendingWith = detail ? formatPendingWith(detail.pending_with) : "";
  const approvedBy = detail ? formatApprovedBy(detail) : "";

  return (
    <Stack gap="md">
      {/* Top bar: week picker + status badge + spacer + actions.

          Перенос разрешён при ЛЮБОЙ ширине (28.09). С v0.16 на десктопе стоял
          `nowrap`: строке нужно около 1130 px, и при окне примерно 769–1383 px
          страница становилась шире окна, кнопки справа уезжали за край.
          Порядок переноса: сначала блок действий уходит на вторую строку
          ЦЕЛИКОМ (он — один flex-элемент с шириной по содержимому) и на
          десктопе прижимается вправо; кнопки внутри блока переносятся по
          одной, только если блок не помещается даже на отдельной строке. */}
      <Group justify="space-between" align="center" wrap="wrap" data-testid="timesheet-toolbar">
        {/* Бейдж статуса («Возврат от ЛР») переносится под навигацию недели,
            если им тесно, — иначе они сжимают друг друга и текст клиппится. */}
        <Group gap="md" wrap="wrap" data-testid="timesheet-toolbar-week">
          <WeekPicker weekStart={weekStart} onChange={setWeekStart} />
          {detail && (
            <StatusBadge
              entity="timesheet"
              code={detail.status as TimesheetStatus}
              label={detail.status_display}
              variant="dot"
              size="sm"
            />
          )}
        </Group>

        {/* На телефоне раскладка прежняя (блок у левого края), на десктопе
            перенесённый блок действий остаётся справа. */}
        <Group
          gap="xs"
          wrap="wrap"
          justify={isMobile ? undefined : "flex-end"}
          ml={isMobile ? undefined : "auto"}
          data-testid="timesheet-toolbar-actions"
        >
          {isEditable && detail && !isAbsenceOnly && (
            <Tooltip
              label="Скопировать проекты и часы прошлой недели. Отправка — отдельной кнопкой."
              withArrow
              position="bottom"
            >
              <Button
                variant="default"
                onClick={handleCopyPreviousWeek}
                loading={copying}
                leftSection={<IconRotateClockwise size={14} />}
                data-testid="copy-previous-week"
              >
                Повторить прошлую неделю
              </Button>
            </Tooltip>
          )}
          {isEditable && detail && !isAbsenceOnly && (
            <AddProjectButton
              projects={projects}
              usedProjectIds={usedProjectIds}
              weekId={detail.id}
              weekStart={weekStart}
              onAdded={reload}
              absenceCoveredDates={absenceCoveredDates}
              workingDayIndices={workingDayIndices}
              onError={setError}
            />
          )}
          {isEditable && detail && (
            <Tooltip
              label="Заполните часы по проектам или добавьте отсутствие — отправлять пока нечего."
              disabled={!isEmptyWeek}
              withArrow
              position="bottom"
            >
              {/* span-wrapper нужен для tooltip над disabled-кнопкой
                  (Mantine v7 не пробрасывает события через disabled). */}
              <span style={{ display: "inline-flex" }}>
                <Button
                  onClick={handleSubmit}
                  loading={loading}
                  disabled={!canSubmit}
                  leftSection={<IconSend size={14} />}
                  color="brand"
                  data-empty-week={isEmptyWeek || undefined}
                >
                  Отправить на согласование
                </Button>
              </span>
            </Tooltip>
          )}
        </Group>
      </Group>

      {pendingFixWeeks.length > 0 && (
        <Alert color="orange" data-testid="pending-fix-alert">
          <Group justify="space-between" align="center" wrap="wrap" gap="xs">
            <Text fz="sm">
              Ждёт исправления: неделя{" "}
              <Text span fw={600}>
                {formatWeekRange(pendingFixWeeks[0].week_start)}
              </Text>{" "}
              ({ACTION_WEEK_REASON[pendingFixWeeks[0].status] ?? "требует правки"})
              {pendingFixWeeks.length > 1 ? ` · и ещё ${pendingFixWeeks.length - 1}` : ""}
            </Text>
            <Button
              size="xs"
              variant="light"
              color="orange"
              onClick={() => setWeekStart(getMonday(dayjs(pendingFixWeeks[0].week_start)))}
              data-testid="pending-fix-open"
            >
              Открыть
            </Button>
          </Group>
        </Alert>
      )}

      {error && (
        <Alert color="red" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {copyInfo && (
        <Alert color="teal" withCloseButton onClose={() => setCopyInfo(null)}>
          {copyInfo}
        </Alert>
      )}

      {loading && (
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      )}

      {/* Сбой загрузки ≠ «недели нет». Раньше обе ситуации давали один экран
          «Таймшит ещё не создан», и отказ сервера (503 при rate-limit) читался
          как «мои часы пропали». Пока не знаем — не утверждаем. */}
      {!loading && !detail && loadFailed && (
        <Stack align="center" py="xl" gap={6} data-testid="timesheet-load-error">
          <Text fw={500}>Не удалось загрузить таймшит</Text>
          <Text c="dimmed" size="sm" ta="center" maw={460}>
            {loadFailed}
          </Text>
          <Text c="dimmed" size="xs" ta="center" maw={460}>
            Данные не потеряны — их не удалось получить. Попробуйте ещё раз.
          </Text>
          <Button variant="light" onClick={retryLoad} mt={4}>
            Повторить
          </Button>
        </Stack>
      )}

      {!loading && !detail && !loadFailed && (
        <Stack align="center" py="xl">
          <Text c="dimmed">Таймшит на эту неделю ещё не создан.</Text>
          <Button onClick={handleCreate} color="brand">
            Создать таймшит
          </Button>
        </Stack>
      )}

      {!loading && detail && (
        <>
          {/*
            US-110. Плашка стоит ПЕРВОЙ и показывается в любом статусе.

            Писем об автозаполнении решено не слать (решение владелицы 15.09),
            поэтому для сотрудника это единственный способ узнать, что неделю
            за него начала система. Незаметный бейдж тут не годится: человек
            может вообще не открывать таймшит — именно поэтому за него и
            заполняют.
          */}
          {detail.autofilled_at && (
            <StatusBanner
              kind="info"
              title="Неделю заполнила система"
              body={
                isEditable
                  ? "Часы списаны по вашему правилу распределения затрат. " +
                    "Неделя ещё не отправлена — проверьте часы и отправьте, " +
                    "либо исправьте, если они не соответствуют работе."
                  : detail.status === "approved"
                    ? "Часы списаны по вашему правилу распределения затрат и " +
                      "уже согласованы. Если они не соответствуют работе, " +
                      "обратитесь к администратору: вернуть согласованную " +
                      "неделю руководитель проекта уже не может."
                    : "Часы списаны по вашему правилу распределения затрат и " +
                      "отправлены на согласование. Если они не соответствуют " +
                      "работе, попросите руководителя проекта вернуть неделю — " +
                      "после возврата её можно править."
              }
              icon={<IconClock size={16} />}
            />
          )}
          {detail.status === "rework_requested_by_line_manager" && (
            <StatusBanner
              kind="rework"
              title="Возврат от линейного руководителя"
              body={detail.lr_comment || "Комментарий не указан."}
              icon={<IconRotateClockwise size={16} />}
            />
          )}
          {detail.status === "rework_requested_by_project_manager" && (
            <StatusBanner
              kind="rework"
              title="Возврат от руководителя проекта"
              body={detail.pm_comment || "Комментарий не указан."}
              meta={
                (detail.approved_project_ids ?? []).length > 0
                  ? "Подписи других РП сохранены (строки с меткой «✓ согласовано») — " +
                    "заново согласует только вернувший РП и те, чьи строки вы измените."
                  : "После исправления отправьте неделю снова — она уйдёт на согласование РП."
              }
              icon={<IconRotateClockwise size={16} />}
            />
          )}
          {detail.status === "submitted_to_line_manager" && (
            <StatusBanner
              kind="info"
              title="На согласовании у линейного руководителя"
              body={
                pendingWith
                  ? `На согласовании у: ${pendingWith}. Поля заблокированы.`
                  : "Неделя отправлена и ждёт решения ЛР. Поля заблокированы."
              }
              icon={<IconClock size={16} />}
            />
          )}
          {detail.status === "waiting_project_managers" && (
            <StatusBanner
              kind="info"
              title="На согласовании у руководителей проектов"
              body={
                // US-85.1: текст для сотрудника — без внутреннего жаргона про
                // конфиг ЛР-этапа (это деталь администрирования, работнику не
                // нужна). Показываем ФИО PM, у кого неделя на согласовании;
                // если PM проектов недели не назначен — общий текст.
                (pendingWith
                  ? `На согласовании у: ${pendingWith}.`
                  : "Неделя ожидает согласования руководителей проектов.") +
                // `after_pm` (заготовка на будущее) — подсказать, что после PM
                // неделя пойдёт к линейному руководителю.
                (lrStage === "after_pm" ? " Затем — у линейного руководителя." : "")
              }
              icon={<IconClock size={16} />}
            />
          )}
          {detail.status === "approved" && (
            <StatusBanner
              kind="approved"
              title="Неделя согласована"
              body={
                approvedBy
                  ? `Согласовано — ${approvedBy}. Неделя закрыта.`
                  : "Все руководители проектов одобрили часы — неделя закрыта."
              }
              icon={<IconCheck size={16} />}
            />
          )}
          {detail.status === "rolled_back_by_admin" && (
            <StatusBanner
              kind="rework"
              title="Откат администратором"
              body="Администратор вернул неделю — можно править часы и отправить заново."
              icon={<IconRotateClockwise size={16} />}
            />
          )}

          {isAbsenceOnly ? (
            <AbsenceOnlyCard />
          ) : (
            <>
              {/* KPI cards */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${isMobile ? 2 : 4}, 1fr)`,
                  gap: 12,
                }}
              >
                <SummaryCard
                  label="Норма недели"
                  value={formatHours(displayedRequired)}
                  unit="ч"
                  // US-74 hotfix: показываем календарную норму отдельно, если
                  // часть недели покрыта approved-отсутствием. Иначе KPI=40 при
                  // фактической нагрузке 24ч путал пользователя.
                  hint={
                    absenceHoursInWeek > 0
                      ? `календарная норма ${formatHours(required)}ч − ${formatHours(absenceHoursInWeek)}ч отсутствий`
                      : "по производственному календарю и доле ставки"
                  }
                />
                <SummaryCard
                  label="Списано"
                  value={formatHours(allocated)}
                  unit="ч"
                  hint={
                    allocated === displayedRequired
                      ? "ровно норма"
                      : allocated < displayedRequired
                        ? "меньше нормы"
                        : "больше нормы"
                  }
                  tone={allocated === displayedRequired ? "accent" : undefined}
                />
                <SummaryCard
                  label={
                    unallocated > 0
                      ? "Осталось списать"
                      : unallocated < 0
                        ? "Переработка"
                        : "Распределено"
                  }
                  value={formatHours(Math.abs(unallocated))}
                  unit="ч"
                  hint={
                    unallocated === 0
                      ? "неделя закрыта"
                      : unallocated > 0
                        ? remainingHint(unallocated, pendingAbsence)
                        : "сверх нормы"
                  }
                  tone={unallocated > 0 ? "warn" : unallocated === 0 ? "accent" : undefined}
                />
                <SummaryCard
                  label="Дедлайн отправки"
                  value={SUBMIT_DEADLINE_LABEL}
                  hint="следующего понедельника"
                />
              </div>

              {/* Мобильный переключатель режима: список / heatmap-сетка. */}
              {isMobile && (
                <Group justify="flex-end" mb="xs">
                  <SegmentedControl
                    size="xs"
                    value={mobileMode}
                    onChange={(v) => setMobileModePersisted(v as MobileMode)}
                    data={[
                      { label: "Список", value: "list" },
                      { label: "Сетка", value: "grid" },
                    ]}
                    data-testid="mobile-mode-toggle"
                  />
                </Group>
              )}

              {/* Grid */}
              {isMobile && mobileMode === "grid" ? (
                <MobileHeatmapGrid
                  rows={rows}
                  dayDates={dayDates}
                  dayNorms={dayNorms}
                  todayIdx={todayIdx}
                  absenceCoveredDates={absenceCoveredDates}
                  weekId={detail.id}
                  isEditable={isEditable && !isAbsenceOnly}
                  neutrals={neutrals}
                  onChanged={reload}
                />
              ) : isMobile ? (
                /* Мобильный список по дням (day-major) — вместо широкой сетки. */
                <Stack gap="sm" data-testid="mobile-timesheet-list">
                  {rows.length === 0 && detail.absences.length === 0 && (
                    <Text c="dimmed" ta="center" py="md" size="sm">
                      Пока нет записей. Добавьте проект кнопкой «Добавить проект» выше.
                    </Text>
                  )}
                  {dayDates.map((date, dayIdx) => {
                    const iso = date.format("YYYY-MM-DD");
                    const nonWork = isNonWorkingDay(dayIdx);
                    const covered = absenceCoveredDates.has(iso);
                    const anyEntry = rows.some((r) => r.cells[dayIdx]);
                    const isTodayCol = dayIdx === todayIdx;
                    // Рабочий день — показываем все проекты недели (можно вносить
                    // в любой). Нерабочий — только уже заполненные (обычно пусто).
                    const visibleRows = nonWork ? rows.filter((r) => r.cells[dayIdx]) : rows;
                    return (
                      <div
                        key={iso}
                        style={{
                          border: `1px solid ${neutrals.border}`,
                          borderRadius: 8,
                          background: nonWork ? neutrals.surfaceAlt : neutrals.surface,
                          overflow: "hidden",
                        }}
                      >
                        <Group
                          justify="space-between"
                          wrap="nowrap"
                          px="sm"
                          py={8}
                          style={{
                            borderBottom:
                              visibleRows.length > 0 ? `1px solid ${neutrals.border}` : undefined,
                            background: isTodayCol ? neutrals.todayBg : undefined,
                          }}
                        >
                          <Group gap={8} wrap="nowrap">
                            <Text fw={600} size="sm" c={nonWork ? neutrals.textDim : neutrals.text}>
                              {DAY_LABELS[dayIdx]} · {date.format("DD.MM")}
                            </Text>
                            {isTodayCol && (
                              <span
                                style={{
                                  fontSize: 10,
                                  fontWeight: 600,
                                  color: "var(--mantine-color-brand-6)",
                                  border: "1px solid var(--mantine-color-brand-3)",
                                  borderRadius: 4,
                                  padding: "1px 5px",
                                }}
                              >
                                сегодня
                              </span>
                            )}
                          </Group>
                          <Text
                            size="sm"
                            fw={600}
                            c={nonWork ? neutrals.textDim : neutrals.text}
                            style={{ fontFamily: "var(--mantine-font-family-monospace)" }}
                          >
                            {nonWork && !anyEntry
                              ? covered
                                ? "отсутствие"
                                : "выходной"
                              : `${formatHours(dayTotals[dayIdx])} ч`}
                          </Text>
                        </Group>
                        {visibleRows.length > 0 && (
                          <Stack gap={0} p="xs">
                            {visibleRows.map((row) => (
                              <Group
                                key={row.projectId}
                                gap={10}
                                wrap="nowrap"
                                style={{ padding: "5px 2px" }}
                              >
                                <span
                                  aria-hidden="true"
                                  style={{
                                    width: 3,
                                    height: 22,
                                    borderRadius: 2,
                                    background: row.projectColor,
                                    flex: "0 0 auto",
                                  }}
                                />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <Text
                                    size="sm"
                                    style={{ fontFamily: "var(--mantine-font-family-monospace)" }}
                                    truncate
                                  >
                                    {row.projectCode}
                                  </Text>
                                  <Text size="xs" c="dimmed" truncate>
                                    {row.projectName}
                                  </Text>
                                </div>
                                <div style={{ flex: "0 0 auto" }}>
                                  <EntryCell
                                    entry={row.cells[dayIdx]}
                                    weekId={detail.id}
                                    projectId={row.projectId}
                                    date={iso}
                                    readonly={!isEditable || nonWork || covered}
                                    isWeekend={nonWork}
                                    isToday={isTodayCol}
                                    isAbsenceCovered={covered}
                                    onChanged={reload}
                                  />
                                </div>
                              </Group>
                            ))}
                          </Stack>
                        )}
                      </div>
                    );
                  })}
                  {detail.absences.length > 0 && (
                    <div
                      style={{
                        border: `1px solid ${neutrals.border}`,
                        borderRadius: 8,
                        background: neutrals.surfaceAlt,
                        padding: "10px 12px",
                      }}
                    >
                      <Text size="xs" c="dimmed" mb={6}>
                        Отсутствия на этой неделе
                      </Text>
                      <Stack gap={4}>
                        {detail.absences.map((absence) => (
                          <Group key={`m-abs-${absence.id}`} gap={8} wrap="nowrap">
                            <span
                              aria-hidden="true"
                              style={{
                                width: 3,
                                height: 16,
                                borderRadius: 2,
                                background: ABSENCE_FALLBACK_COLOR[absence.absence_type],
                                flex: "0 0 auto",
                              }}
                            />
                            <IconCalendarOff size={13} />
                            <Text size="sm" style={{ flex: 1, minWidth: 0 }} truncate>
                              {absence.absence_type_display}
                            </Text>
                            <Text
                              size="xs"
                              c="dimmed"
                              style={{ fontFamily: "var(--mantine-font-family-monospace)" }}
                            >
                              {formatAbsenceHours(absence.hours_in_week)} ч
                            </Text>
                          </Group>
                        ))}
                      </Stack>
                    </div>
                  )}
                </Stack>
              ) : (
                <Box style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                  <div
                    data-testid="timesheet-grid"
                    style={{
                      border: `1px solid ${neutrals.border}`,
                      borderRadius: 8,
                      background: neutrals.surface,
                      overflow: "hidden",
                      minWidth: 720,
                    }}
                  >
                    {/* Header row */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: GRID_COLUMNS,
                        borderBottom: `1px solid ${neutrals.border}`,
                        background: neutrals.surfaceAlt,
                        fontSize: 11,
                        color: neutrals.textMuted,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        fontWeight: 500,
                      }}
                    >
                      <div
                        style={{
                          padding: "10px 14px",
                          borderRight: `1px solid ${neutrals.border}`,
                        }}
                      >
                        Проект
                      </div>
                      {dayDates.map((d, i) => {
                        // US-53: визуально различаем holiday/transfer/shortened.
                        // Нерабочие — серый фон + тусклая дата. Сокращённый — точка-маркер.
                        const isNonWork = isNonWorkingDay(i);
                        const info = dayInfos[i];
                        const isShortened = info?.day_type === "shortened";
                        const tooltipLabel = dayTypeLabel(i);
                        const cell = (
                          <div
                            key={i}
                            style={{
                              padding: "8px 4px",
                              textAlign: "center",
                              borderRight: `1px solid ${neutrals.border}`,
                              color: isNonWork ? neutrals.textDim : neutrals.textMuted,
                              background:
                                todayIdx === i
                                  ? neutrals.todayBg
                                  : isNonWork
                                    ? neutrals.surfaceAlt
                                    : undefined,
                              position: "relative",
                            }}
                            aria-label={DAY_FULL[i]}
                            data-day-type={info?.day_type ?? "work"}
                          >
                            <div>{DAY_LABELS[i]}</div>
                            <div
                              style={{
                                fontFamily: "var(--mantine-font-family-monospace)",
                                fontSize: 10.5,
                                color: neutrals.textDim,
                                marginTop: 2,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {d.format("DD.MM")}
                            </div>
                            {isShortened && (
                              <div
                                aria-hidden
                                style={{
                                  position: "absolute",
                                  top: 4,
                                  right: 6,
                                  width: 5,
                                  height: 5,
                                  borderRadius: "50%",
                                  background: "var(--mantine-color-orange-5, #f59f00)",
                                }}
                              />
                            )}
                          </div>
                        );
                        return tooltipLabel ? (
                          <Tooltip key={i} label={tooltipLabel} withArrow position="top">
                            {cell}
                          </Tooltip>
                        ) : (
                          cell
                        );
                      })}
                      <div
                        style={{
                          padding: "10px 4px",
                          textAlign: "center",
                          color: neutrals.text,
                          fontWeight: 600,
                        }}
                      >
                        Итого
                      </div>
                    </div>

                    {/* Project rows */}
                    {rows.map((row, ri) => (
                      <div
                        key={row.projectId}
                        style={{
                          display: "grid",
                          gridTemplateColumns: GRID_COLUMNS,
                          borderBottom:
                            ri === rows.length - 1 && detail.absences.length === 0
                              ? "none"
                              : `1px solid ${neutrals.border}`,
                        }}
                      >
                        <div
                          style={{
                            padding: "8px 14px",
                            borderRight: `1px solid ${neutrals.border}`,
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            minWidth: 0,
                          }}
                        >
                          <span
                            aria-hidden="true"
                            style={{
                              width: 3,
                              height: 18,
                              borderRadius: 1.5,
                              background: row.projectColor,
                              flexShrink: 0,
                            }}
                          />
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div
                              style={{
                                fontSize: 12.5,
                                fontWeight: 500,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                color: neutrals.text,
                              }}
                            >
                              {row.projectName}
                            </div>
                            <div
                              style={{
                                fontSize: 10.5,
                                color: neutrals.textDim,
                                fontFamily: "var(--mantine-font-family-monospace)",
                                display: "flex",
                                gap: 8,
                                marginTop: 1,
                              }}
                            >
                              <span>{row.projectCode}</span>
                              {showApprovedMarks && approvedProjectIds.has(row.projectId) && (
                                <Tooltip
                                  label="Проект уже согласован его РП. Если измените строки — подпись снимется и потребуется повторное согласование."
                                  withArrow
                                  multiline
                                  maw={280}
                                >
                                  <span
                                    data-testid={`approved-mark-${row.projectId}`}
                                    style={{
                                      color: "var(--mantine-color-teal-7)",
                                      fontWeight: 600,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    ✓ согласовано
                                  </span>
                                </Tooltip>
                              )}
                              {row.jiraReference && (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 3,
                                  }}
                                >
                                  <IconExternalLink size={9} />
                                  {row.jiraReference}
                                </span>
                              )}
                            </div>
                          </div>
                          {/* US-45: удаление строки проекта (видна только в редактируемом статусе). */}
                          {isEditable && (
                            <Tooltip label="Удалить строку проекта" withArrow>
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                size="sm"
                                aria-label={`Удалить строку проекта ${row.projectCode}`}
                                onClick={() =>
                                  setDeletingProject({
                                    projectId: row.projectId,
                                    projectCode: row.projectCode,
                                    projectName: row.projectName,
                                    totalHours: row.total,
                                  })
                                }
                                style={{ flexShrink: 0 }}
                              >
                                <IconTrash size={14} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </div>
                        {row.cells.map((cell, di) => (
                          <div key={di} style={{ borderRight: `1px solid ${neutrals.border}` }}>
                            <EntryCell
                              entry={cell}
                              weekId={detail.id}
                              projectId={row.projectId}
                              date={dayDates[di].format("YYYY-MM-DD")}
                              // US-53: нерабочий день (Sat/Sun + праздники + переносы)
                              // → readonly + серый фон. Календарь приходит в detail.days.
                              // US-71: дни approved-отпуска тоже readonly, чтобы
                              // пользователь не пытался списать на покрытый день.
                              readonly={
                                !isEditable ||
                                isNonWorkingDay(di) ||
                                absenceCoveredDates.has(dayDates[di].format("YYYY-MM-DD"))
                              }
                              isWeekend={isNonWorkingDay(di)}
                              isToday={todayIdx === di}
                              isAbsenceCovered={absenceCoveredDates.has(
                                dayDates[di].format("YYYY-MM-DD"),
                              )}
                              onChanged={reload}
                            />
                          </div>
                        ))}
                        <div
                          style={{
                            height: ROW_HEIGHT,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontFamily: "var(--mantine-font-family-monospace)",
                            fontSize: 12.5,
                            fontWeight: 600,
                            color: row.total === 0 ? neutrals.textDim : neutrals.text,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {row.total > 0 ? formatHours(row.total) : "—"}
                        </div>
                      </div>
                    ))}

                    {rows.length === 0 && detail.absences.length === 0 && (
                      <div
                        style={{
                          padding: "32px 14px",
                          textAlign: "center",
                          color: neutrals.textDim,
                          fontSize: 13,
                        }}
                      >
                        Нет строк. Добавьте проект через кнопку «+ Добавить проект» сверху.
                      </div>
                    )}

                    {/* Absence rows */}
                    {detail.absences.map((absence, idx) => {
                      const byIndex = new Map<number, string>();
                      for (const d of absence.days) {
                        const i = dayjs(d.date).startOf("day").diff(weekStart, "day");
                        if (i >= 0 && i < 7) byIndex.set(i, formatAbsenceHours(d.hours));
                      }
                      const totalLabel = formatAbsenceHours(absence.hours_in_week);
                      const stripeColor = ABSENCE_FALLBACK_COLOR[absence.absence_type];

                      return (
                        <div
                          key={`absence-${absence.id}`}
                          data-testid={`absence-row-${absence.absence_type}`}
                          data-absence-id={absence.id}
                          style={{
                            display: "grid",
                            gridTemplateColumns: GRID_COLUMNS,
                            borderTop: idx === 0 ? `1px solid ${neutrals.border}` : undefined,
                            borderBottom:
                              idx === detail.absences.length - 1
                                ? undefined
                                : `1px solid ${neutrals.border}`,
                            background: neutrals.surfaceAlt,
                          }}
                        >
                          <div
                            style={{
                              padding: "8px 14px",
                              borderRight: `1px solid ${neutrals.border}`,
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              color: neutrals.textMuted,
                              fontSize: 12.5,
                              minWidth: 0,
                            }}
                          >
                            <span
                              aria-hidden="true"
                              style={{
                                width: 3,
                                height: 18,
                                borderRadius: 1.5,
                                background: stripeColor,
                                flexShrink: 0,
                              }}
                            />
                            <IconCalendarOff size={13} />
                            <span style={{ flex: 1 }}>{absence.absence_type_display}</span>
                            {/* US-86: бейдж «auto» = строка подставлена автоматически
                              из одобренной заявки на отсутствие (часы посчитаны по
                              календарю, править нельзя). НЕ про автосогласование. */}
                            <Tooltip
                              label="Подставлено автоматически из одобренной заявки на отсутствие. Часы посчитаны по производственному календарю — строку нельзя редактировать."
                              multiline
                              maw={260}
                              withArrow
                              position="top"
                            >
                              <span
                                style={{
                                  fontFamily: "var(--mantine-font-family-monospace)",
                                  fontSize: 10,
                                  color: neutrals.textDim,
                                  padding: "2px 6px",
                                  borderRadius: 4,
                                  background: neutrals.surface,
                                  border: `1px solid ${neutrals.border}`,
                                  cursor: "help",
                                }}
                              >
                                auto
                              </span>
                            </Tooltip>
                          </div>
                          {Array.from({ length: 7 }, (_, di) => (
                            <div
                              key={di}
                              style={{
                                height: ROW_HEIGHT,
                                borderRight: `1px solid ${neutrals.border}`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontFamily: "var(--mantine-font-family-monospace)",
                                fontSize: 12.5,
                                color: byIndex.has(di) ? neutrals.text : neutrals.textDim,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {byIndex.get(di) ?? ""}
                            </div>
                          ))}
                          <div
                            style={{
                              height: ROW_HEIGHT,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontFamily: "var(--mantine-font-family-monospace)",
                              fontSize: 12.5,
                              fontWeight: 600,
                              color: neutrals.text,
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {totalLabel}
                          </div>
                        </div>
                      );
                    })}

                    {/* Totals row */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: GRID_COLUMNS,
                        borderTop: `1px solid ${neutrals.borderStrong}`,
                        background: neutrals.surfaceAlt,
                        fontWeight: 600,
                      }}
                    >
                      <div
                        style={{
                          padding: "10px 14px",
                          borderRight: `1px solid ${neutrals.border}`,
                          fontSize: 11.5,
                          textTransform: "uppercase",
                          color: neutrals.textMuted,
                          letterSpacing: "0.04em",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Итого
                      </div>
                      {dayTotals.map((dt, di) => {
                        const status = STATUS[dark ? "dark" : "light"];
                        // US-53: считаем нерабочим всё с нулевой нормой (Sat/Sun + holiday + transfer).
                        const isWeekend = isNonWorkingDay(di);
                        const isToday = todayIdx === di;
                        const color = isWeekend
                          ? neutrals.textDim
                          : dt > 0 && dt < 8
                            ? status.rework.fg
                            : neutrals.text;
                        return (
                          <div
                            key={di}
                            style={{
                              height: ROW_HEIGHT,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRight: `1px solid ${neutrals.border}`,
                              fontFamily: "var(--mantine-font-family-monospace)",
                              fontSize: 12.5,
                              color,
                              fontVariantNumeric: "tabular-nums",
                              background: isToday ? neutrals.todayBg : undefined,
                            }}
                          >
                            {dt > 0 ? formatHours(dt) : isWeekend ? "—" : "0"}
                          </div>
                        );
                      })}
                      <div
                        style={{
                          height: ROW_HEIGHT,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontFamily: "var(--mantine-font-family-monospace)",
                          fontSize: 13.5,
                          fontWeight: 700,
                          color:
                            allocated === displayedRequired
                              ? STATUS[dark ? "dark" : "light"].approved.fg
                              : allocated < displayedRequired
                                ? STATUS[dark ? "dark" : "light"].rework.fg
                                : neutrals.text,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {/* US-74.1: ИТОГО показывает allocated / displayedRequired
                          (норма с вычетом отсутствий), а не календарную норму. */}
                        {formatHours(allocated)} / {formatHours(displayedRequired)}
                      </div>
                    </div>
                  </div>
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* US-46: подтверждение отправки с unallocated != 0.
          unallocated > 0  → недосписано
          unallocated < 0  → переработка */}
      <Modal
        opened={submitConfirmOpen}
        onClose={() => {
          if (!loading) setSubmitConfirmOpen(false);
        }}
        title="Отправить таймшит?"
        centered
      >
        {detail && (
          <Stack>
            {unallocated > 0 ? (
              <>
                <Text size="sm">
                  Списано <strong>{formatHours(allocated)}</strong> из{" "}
                  <strong>{formatHours(required)}</strong> часов (на{" "}
                  <strong>{formatHours(unallocated)}</strong> ч меньше нормы).
                </Text>
                {pendingAbsence > 0 ? (
                  <Text size="sm" c="dimmed">
                    <strong>{formatHours(Math.min(pendingAbsence, unallocated))}</strong> ч
                    приходятся на дни заявки на отсутствие, ожидающей согласования. Уточните статус
                    согласования.
                    {unallocated - pendingAbsence > 0.001 && (
                      <>
                        {" "}
                        Остальные <strong>{formatHours(unallocated - pendingAbsence)}</strong> ч —
                        дни, на которые часы не проставлены.
                      </>
                    )}
                  </Text>
                ) : (
                  <Text size="sm" c="dimmed">
                    Часто это значит, что забыли проставить часы за какой-то день. Точно отправить
                    на согласование?
                  </Text>
                )}
              </>
            ) : (
              <>
                <Text size="sm">
                  Списано <strong>{formatHours(allocated)}</strong> из{" "}
                  <strong>{formatHours(required)}</strong> часов (на{" "}
                  <strong>{formatHours(Math.abs(unallocated))}</strong> ч больше нормы).
                </Text>
                <Text size="sm" c="dimmed">
                  Если это переработка — стоит добавить комментарий к строкам, где она была. Точно
                  отправить на согласование?
                </Text>
              </>
            )}
            <Group justify="flex-end" gap="xs">
              <Button
                variant="default"
                onClick={() => setSubmitConfirmOpen(false)}
                disabled={loading}
              >
                Дополнить
              </Button>
              <Button color="brand" onClick={executeSubmit} loading={loading}>
                Всё равно отправить
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* US-45: подтверждение удаления строки проекта */}
      <Modal
        opened={!!deletingProject}
        onClose={() => {
          if (!deletingBusy) setDeletingProject(null);
        }}
        title="Удалить строку проекта"
        centered
      >
        {deletingProject && (
          <Stack>
            <Text size="sm">
              Удалить строку проекта{" "}
              <strong>
                {deletingProject.projectCode} — {deletingProject.projectName}
              </strong>
              ?
              {deletingProject.totalHours > 0 && (
                <>
                  {" "}
                  Со всеми списанными часами ({formatHours(deletingProject.totalHours)} ч за
                  неделю).
                </>
              )}
            </Text>
            <Group justify="flex-end" gap="xs">
              <Button
                variant="default"
                onClick={() => setDeletingProject(null)}
                disabled={deletingBusy}
              >
                Отмена
              </Button>
              <Button color="red" onClick={handleConfirmDeleteProject} loading={deletingBusy}>
                Удалить
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
